import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Target, ChevronRight } from "lucide-react";
import InstructorBottomNav from "../components/instructor/InstructorBottomNav";
import { InstructorStats } from "../components/instructor/InstructorStats";
import InstructorTrustStack from "../components/instructor/InstructorTrustStack";
import MyTeachingExcellence from "../components/instructor/MyTeachingExcellence";
import InstructorChecksWidget from "../components/instructor/InstructorChecksWidget";
import ComplianceWidget from "../components/instructor/ComplianceWidget";
import PendingTestResultsCard from "../components/instructor/PendingTestResultsCard";
import { GapMetrics } from "./GapRecovery";
import GrowthGoalsPrompt from "../components/GrowthGoalsPrompt";
import ShareProfileButton from "../components/instructor/ShareProfileButton";
import { ReadinessCard, CompetencyBreakdown, EndOfDayCard, SupervisorCard } from "./PdiCards";
import UpgradePrompt from "../components/instructor/UpgradePrompt";
import AsyncSurface from "../components/AsyncSurface";
import CardErrorBoundary from "../components/CardErrorBoundary";
import { useInstructorTier } from "../hooks/useInstructorTier";
import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * InstructorProgress — Phase 11.2 (Feb 6, 2026): the "Progress" tab.
 *
 * Tracking + control only. Performance metrics, DVSA standards,
 * compliance, credentials, oversight.
 *
 * Per locked map (Feb 6, 2026 confirmation):
 *   • Lessons taught / hours = here (from split InstructorStats)
 *   • Revenue figures = Money tab (do NOT mix)
 *   • Fill rate (GapMetrics) = here, not Money — performance metric
 *   • CPD = Training, not here
 *   • When activated, Growth surfaces → tracked here
 */
export default function InstructorProgress() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { has } = useInstructorTier();
  const role = (user?.role || "adi").toLowerCase();
  const isPdi = role === "pdi";

  const [readiness, setReadiness] = useState(null);
  const [todayEod, setTodayEod] = useState(null);
  const [showEod, setShowEod] = useState(false);
  // Phase 1.3 alive-contract — readiness + EOD fetch states are
  // tracked truthfully so the PDI cards (ReadinessCard +
  // CompetencyBreakdown, which previously returned null when
  // readiness was missing) can render a calm AsyncSurface state
  // instead of silently disappearing.
  const [pdiLoading, setPdiLoading] = useState(false);
  const [pdiError, setPdiError] = useState(null);

  const loadPdi = useCallback(async () => {
    setPdiLoading(true);
    setPdiError(null);
    try {
      const [rr, ee] = await Promise.allSettled([
        axios.get(`${API}/api/pdi/readiness`, { withCredentials: true }),
        axios.get(`${API}/api/pdi/eod/today`,  { withCredentials: true }),
      ]);
      // Readiness is the primary surface — its failure must be
      // truthful. EOD is decorative — its failure is silent (no
      // operational impact for the user).
      if (rr.status === "fulfilled") {
        setReadiness(rr.value?.data || null);
      } else {
        setReadiness(null);
        setPdiError(rr.reason?.message || "Couldn't load your readiness data.");
      }
      setTodayEod(ee.status === "fulfilled" ? (ee.value?.data || null) : null);
    } finally {
      setPdiLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isPdi) return;
    loadPdi();
  }, [isPdi, loadPdi]);

  return (
    <div data-testid="instructor-progress-page" className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="progress-back-btn" onClick={() => nav(isPdi ? "/pdi-hub" : "/instructor")} className="text-slate-400 hover:text-slate-700 dark:hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Target className="w-5 h-5 text-amber-600" />
          <p className="font-extrabold text-slate-900 dark:text-white text-base">Progress</p>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-4 space-y-4">
        {/* Basic stats — visible to all tiers. */}
        <InstructorStats variant="performance" testidPrefix="progress-stats" />

        {(user?.id || user?._id) && (
          <section data-testid="progress-trust-section" className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Your trust profile · what pupils see</p>
            <InstructorTrustStack instructorId={user.id || user._id} max={6} />
          </section>
        )}

        {/* Full Readiness dashboard + advanced progress insights —
            Premium+ only for ADIs. PDIs always see this (it's their
            Part-3 prep, the core of their use case — independent of
            tier). Feb 6, 2026: Basic ADI users see ONE clean inline
            upgrade card here, per owner directive. */}
        {(isPdi || has("premium")) ? (
          <>
            <MyTeachingExcellence />

            {isPdi ? (
              <CardErrorBoundary region="pdi-readiness" label="Your Part 3 readiness">
                <section data-testid="progress-pdi-section" className="space-y-3">
                  <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">Part 3 readiness</h2>
                  <AsyncSurface
                    testid="pdi-readiness"
                    loading={pdiLoading && !readiness}
                    error={pdiError}
                    empty={!pdiLoading && !pdiError && !readiness}
                    loadingLabel="Loading your readiness…"
                    emptyLabel="No readiness data yet — debrief a lesson to build your Part 3 picture."
                    onRetry={loadPdi}
                  >
                    <ReadinessCard readiness={readiness} passInfo={null} />
                    <CompetencyBreakdown readiness={readiness} />
                  </AsyncSurface>
                  <EndOfDayCard todayEod={todayEod} onDone={() => {}} open={showEod} setOpen={setShowEod} />
                  <CardErrorBoundary region="pdi-supervisor" label="Your supervisor card">
                    <SupervisorCard />
                  </CardErrorBoundary>
                  <Tile testid="progress-tile-trainer-path" label="Road to becoming an ADI" desc="Plan your move" onClick={() => nav("/trainer-path")} />
                </section>
              </CardErrorBoundary>
            ) : (
              <section data-testid="progress-adi-section" className="space-y-2">
                <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">Pass-rate inputs</h2>
                <PendingTestResultsCard />
                <GapMetrics />
              </section>
            )}
          </>
        ) : (
          <UpgradePrompt
            testId="progress-readiness-upgrade"
            title="Progress tracking"
            upgradeTier="Premium"
            bullets={[
              "Full Readiness dashboard with trends",
              "Advanced progress insights per pupil",
              "Deeper coaching + analysis tools",
            ]}
          />
        )}

        <section data-testid="progress-compliance-section" className="space-y-2">
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">Status & credentials</h2>
          <InstructorChecksWidget />
          <ComplianceWidget />
          <Tile testid="progress-tile-compliance" label="Compliance"           desc="Tax · MOT · insurance reminders"  onClick={() => nav("/instructor/compliance")} />
          <Tile testid="progress-tile-standards"  label="Standards Check"      desc="Track ADI Part 3 readiness"      onClick={() => nav("/instructor/standards")} />
          <Tile testid="progress-tile-passport"   label="Instructor Passport"  desc="Your verified profile"           onClick={() => nav("/instructor/passport")} />
          <Tile testid="progress-tile-licences"   label="Licence checks"       desc="Pupil licence verification"      onClick={() => nav("/instructor/licences")} />
          <Tile testid="progress-tile-identity"   label="Identity verification" desc="Email · phone · DBS"            onClick={() => nav("/settings/identity")} />
        </section>

        <section data-testid="progress-reputation-section" className="space-y-2">
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">Reputation & visibility</h2>
          <ShareProfileButton />
          <Tile testid="progress-tile-marketplace"   label="Marketplace settings"   desc="How you appear to new pupils" onClick={() => nav("/settings/marketplace")} />
          <Tile testid="progress-tile-weekly-review" label="Weekly review prefs"    desc="When your weekly summary lands" onClick={() => nav("/instructor/weekly-review-prefs")} />
        </section>

        <section data-testid="progress-growth-section" className="space-y-2">
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">Growth tracking</h2>
          <GrowthGoalsPrompt />
          <Tile testid="progress-tile-growth-ladder" label="Growth ladder"   desc="Career progression milestones" onClick={() => nav("/settings/growth-ladder")} />
          <Tile testid="progress-tile-trainer-path"  label="Become a trainer" desc="Mentor the next generation"   onClick={() => nav("/trainer-path")} />
        </section>
      </main>

      <InstructorBottomNav role={role} active="progress" />
    </div>
  );
}

function Tile({ testid, label, desc, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick} className="w-full text-left rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 flex items-center gap-3 hover:border-amber-300 active:scale-[0.99] transition-all">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-slate-900 dark:text-white">{label}</p>
        <p className="text-[11px] text-slate-500 mt-0.5">{desc}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
    </button>
  );
}
